import { CustomerDetailDialog } from '@/components/Customers/CustomerDetailDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  type DuplicateCustomerSignal,
  type ListCustomersParams,
  createDuplicateSignals,
} from '@/contracts/customers'
import { useAuth } from '@/lib/auth'
import type { CustomerManagementViewMode } from '@/lib/b2b-customer-workflow-ui'
import {
  B2B_PAGE_SIZE_OPTIONS,
  type B2bPageSize,
  CUSTOMER_MANAGEMENT_DEFAULT_FILING_FILTER,
  CUSTOMER_MANAGEMENT_PAGE_SIZE,
} from '@/lib/b2b-list-defaults'
import {
  type BusinessRole,
  type CustomerApplication,
  type CustomerFilingStatus,
  type ManagedCustomer,
  type SalesUserRole,
  canAccessCustomerManagement,
  canBackfillCustomerData,
  customerFilingStatusMeta,
  getBusinessRole,
  getSalesUserByEmail,
  isFullNewCustomerApplication,
  isSalesContributorRole,
} from '@/lib/business-workflow'
import { createCustomerDuplicateCandidates } from '@/lib/customer-duplicate-candidates'
import { clearCustomerListPageCache } from '@/lib/customer-list-page-cache'
import {
  compareErpCustomerBackfill,
  getCustomer,
  listCustomerDuplicateCandidates,
  listCustomersWithPageCache,
} from '@/lib/data-client/customers'
import { isApiDataClientMode } from '@/lib/data-client/mode'
import { useBusinessWorkflowDataClientHydration } from '@/lib/data-client/use-business-workflow-hydration'
import { useSystemFeatureVisibility } from '@/lib/system-feature-visibility'
import { useBusinessWorkflowStore } from '@/stores/businessWorkflowStore'
import { useQuery } from '@tanstack/react-query'
import { Archive, ArchiveRestore, Building2, ClipboardList, RefreshCw, Search } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

type ViewMode = CustomerManagementViewMode
type CustomerListSort = NonNullable<ListCustomersParams['sort']>
type CustomerArchiveStatusFilter = NonNullable<ListCustomersParams['status']>
type CustomerWorkflowFilter = NonNullable<ListCustomersParams['customerWorkflow']>

const customerSortOptions: Array<{ value: CustomerListSort; label: string }> = [
  { value: 'updated_desc', label: '最近更新優先' },
  { value: 'updated_asc', label: '最早更新優先' },
  { value: 'customer_code_asc', label: '客戶代號 A-Z' },
  { value: 'customer_code_desc', label: '客戶代號 Z-A' },
  { value: 'name_asc', label: '名稱 A-Z' },
  { value: 'name_desc', label: '名稱 Z-A' },
  { value: 'owner_asc', label: '負責業務 A-Z' },
]

const customerArchiveStatusOptions: Array<{ value: CustomerArchiveStatusFilter; label: string }> = [
  { value: 'active', label: '未封存' },
  { value: 'archived', label: '已封存' },
  { value: 'all', label: '全部封存狀態' },
]

const customerWorkflowOptions: Array<{ value: CustomerWorkflowFilter; label: string }> = [
  { value: 'all', label: '全部新客戶建檔' },
  { value: 'new_customer', label: '全新客戶' },
  { value: 'existing_customer_change', label: '既有客戶變更資料' },
]

export default function CustomersRoute() {
  const { user, isLoading } = useAuth()
  useBusinessWorkflowDataClientHydration(!isLoading, { includeCustomers: false })
  const salesUsers = useBusinessWorkflowStore((state) => state.salesUsers)
  const applications = useBusinessWorkflowStore((state) => state.applications)
  const customers = useBusinessWorkflowStore((state) => state.customers)
  const sourceLinks = useBusinessWorkflowStore((state) => state.sourceLinks)
  const fieldValuesByApplication = useBusinessWorkflowStore((state) => state.fieldValues)
  const updateCustomer = useBusinessWorkflowStore((state) => state.updateCustomer)
  const featureVisibility = useSystemFeatureVisibility()

  const [keyword, setKeyword] = useState('')
  const [ownerFilter, setOwnerFilter] = useState('all')
  const [filingFilter, setFilingFilter] = useState<'all' | CustomerFilingStatus>(
    CUSTOMER_MANAGEMENT_DEFAULT_FILING_FILTER,
  )
  const [archiveStatusFilter, setArchiveStatusFilter] =
    useState<CustomerArchiveStatusFilter>('active')
  const [customerWorkflowFilter, setCustomerWorkflowFilter] =
    useState<CustomerWorkflowFilter>('new_customer')
  const [customerSort, setCustomerSort] = useState<CustomerListSort>('updated_desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState<B2bPageSize>(CUSTOMER_MANAGEMENT_PAGE_SIZE)
  const [editingCustomer, setEditingCustomer] = useState<ManagedCustomer | null>(null)
  const [loadingCustomerId, setLoadingCustomerId] = useState<string | null>(null)
  const [customerDetailError, setCustomerDetailError] = useState('')
  const [customerArchiveError, setCustomerArchiveError] = useState('')
  const [archivingCustomerId, setArchivingCustomerId] = useState<string | null>(null)
  const [customerSaveStatus, setCustomerSaveStatus] = useState<'idle' | 'saving'>('idle')
  const [customerSaveError, setCustomerSaveError] = useState('')
  const [isRefreshingCustomers, setIsRefreshingCustomers] = useState(false)

  const role = getBusinessRole(user, salesUsers)
  const canBackfillCustomer = canBackfillCustomerData(user, salesUsers)
  const currentSalesUser = getSalesUserByEmail(user?.email, salesUsers)
  const effectiveMode = getEffectiveMode(role, currentSalesUser?.role)
  const persona = getPersonaForMode(effectiveMode, user?.email, salesUsers)

  const ownerOptions = useMemo(
    () =>
      Array.from(
        new Map(
          salesUsers
            .filter((salesUser) => isSalesContributorRole(salesUser.role))
            .map((salesUser) => [salesUser.email, salesUser.name]),
        ).entries(),
      ),
    [salesUsers],
  )

  const customerListParams = useMemo<ListCustomersParams>(
    () => ({
      keyword: keyword.trim() || undefined,
      ownerEmail: ownerFilter === 'all' ? undefined : ownerFilter,
      status: archiveStatusFilter,
      filingStatus: filingFilter,
      customerWorkflow: customerWorkflowFilter,
      sort: customerSort,
      page: currentPage,
      limit: pageSize,
    }),
    [
      archiveStatusFilter,
      currentPage,
      customerSort,
      customerWorkflowFilter,
      filingFilter,
      keyword,
      ownerFilter,
      pageSize,
    ],
  )

  const customerListQuery = useQuery({
    queryKey: ['customers-page', customerListParams],
    queryFn: () => listCustomersWithPageCache(customerListParams),
    enabled: !isLoading && canAccessCustomerManagement(user, salesUsers),
    staleTime: Number.POSITIVE_INFINITY,
  })

  const duplicateCustomerCandidatesQuery = useQuery({
    queryKey: ['customer-duplicate-candidates', editingCustomer?.id],
    queryFn: () => listCustomerDuplicateCandidates(editingCustomer?.id ?? ''),
    enabled: Boolean(
      editingCustomer &&
        isFullNewCustomerApplication(editingCustomer) &&
        (editingCustomer.companyName.trim() || editingCustomer.taxId.trim()),
    ),
    staleTime: 0,
  })

  const pageCustomers = customerListQuery.data?.data ?? []
  const totalCustomers = customerListQuery.data?.meta.total ?? 0
  const pageCount = Math.max(1, Math.ceil(totalCustomers / pageSize))
  const pageCustomersInScope = useMemo(
    () => pageCustomers.filter((customer) => isCustomerInScope(customer, effectiveMode, persona)),
    [effectiveMode, pageCustomers, persona],
  )
  const localDuplicateCustomerPool = useMemo(
    () => dedupeCustomersById([...customers, ...pageCustomers]),
    [customers, pageCustomers],
  )

  const selectedSourceApplication = useMemo(
    () =>
      editingCustomer
        ? applications.find((application) => application.id === editingCustomer.sourceApplicationId)
        : null,
    [applications, editingCustomer],
  )
  const selectedSourceLink = useMemo(
    () =>
      editingCustomer
        ? sourceLinks.find((link) => link.applicationId === editingCustomer.sourceApplicationId)
        : undefined,
    [editingCustomer, sourceLinks],
  )
  const selectedFieldValues = editingCustomer
    ? (fieldValuesByApplication[editingCustomer.sourceApplicationId] ?? [])
    : []

  const selectedDuplicateSignals = useMemo(
    () =>
      featureVisibility['customers.duplicates'] && selectedSourceApplication
        ? mergeDuplicateSignals(
            createDuplicateSignals(
              selectedSourceApplication,
              customers.filter((customer) => customer.id !== editingCustomer?.id),
            ),
            selectedSourceApplication,
            editingCustomer,
          )
        : [],
    [customers, editingCustomer, featureVisibility, selectedSourceApplication],
  )
  const selectedDuplicateCandidates = useMemo(
    () =>
      duplicateCustomerCandidatesQuery.data?.data ??
      (editingCustomer
        ? createCustomerDuplicateCandidates(editingCustomer, localDuplicateCustomerPool)
        : []),
    [duplicateCustomerCandidatesQuery.data?.data, editingCustomer, localDuplicateCustomerPool],
  )
  const paginationResetKey = [
    effectiveMode,
    filingFilter,
    keyword,
    ownerFilter,
    customerSort,
    customerWorkflowFilter,
    archiveStatusFilter,
    pageSize,
    persona?.email ?? '',
  ].join('|')

  useEffect(() => {
    void paginationResetKey
    setCurrentPage(1)
  }, [paginationResetKey])

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, pageCount))
  }, [pageCount])

  useEffect(() => {
    void editingCustomer?.id
    setCustomerSaveError('')
    setCustomerSaveStatus('idle')
  }, [editingCustomer?.id])

  if (isLoading) {
    return <div className="grid min-h-dvh place-items-center text-sm text-slate-500">載入中...</div>
  }

  if (!canAccessCustomerManagement(user, salesUsers)) {
    return (
      <div className="grid min-h-dvh place-items-center bg-slate-50 p-6 text-center">
        <div>
          <h1 className="text-lg font-semibold text-slate-950">沒有客戶管理權限</h1>
          <p className="mt-2 text-sm text-slate-500">你的 Email 尚未列入業務、主管或管理員名單。</p>
        </div>
      </div>
    )
  }

  async function saveCustomer(pendingPatch: Partial<ManagedCustomer> = {}) {
    if (!editingCustomer) return
    setCustomerSaveStatus('saving')
    setCustomerSaveError('')
    try {
      const candidate = {
        ...editingCustomer,
        ...pendingPatch,
      }
      const erpCustomerCode = candidate.erpCustomerCode.trim()
      if (
        !isApiDataClientMode() &&
        erpCustomerCode &&
        customers.some(
          (customer) =>
            customer.id !== candidate.id &&
            customer.erpCustomerCode.trim().toLocaleLowerCase() ===
              erpCustomerCode.toLocaleLowerCase(),
        )
      ) {
        throw new Error(`客戶代號「${erpCustomerCode}」已存在，不能重複回填。`)
      }
      await updateCustomer(candidate.id, candidate)
      clearCustomerListPageCache()
      await customerListQuery.refetch()
      setEditingCustomer(null)
    } catch (error) {
      setCustomerSaveError(
        error instanceof Error ? error.message : '客戶資料儲存失敗，請稍後再試。',
      )
    } finally {
      setCustomerSaveStatus('idle')
    }
  }

  async function openCustomerDetail(customer: ManagedCustomer) {
    setCustomerDetailError('')
    setCustomerSaveError('')
    setLoadingCustomerId(customer.id)
    try {
      const latestCustomer = await getCustomer(customer.id)
      setEditingCustomer({ ...latestCustomer })
    } catch (error) {
      setCustomerDetailError(
        error instanceof Error ? error.message : '讀取客戶最新資料失敗，請稍後再試。',
      )
      setEditingCustomer({ ...customer })
    } finally {
      setLoadingCustomerId(null)
    }
  }

  async function updateCustomerArchiveStatus(
    customer: ManagedCustomer,
    nextStatus: ManagedCustomer['status'],
  ) {
    setCustomerArchiveError('')
    setArchivingCustomerId(customer.id)
    try {
      await updateCustomer(customer.id, {
        ...customer,
        status: nextStatus,
      })
      clearCustomerListPageCache()
      await customerListQuery.refetch()
      await duplicateCustomerCandidatesQuery.refetch()
    } catch (error) {
      setCustomerArchiveError(
        error instanceof Error ? error.message : '客戶封存狀態更新失敗，請稍後再試。',
      )
    } finally {
      setArchivingCustomerId(null)
    }
  }

  async function applyDuplicateCustomerFields(input: { patch: Partial<ManagedCustomer> }) {
    if (!editingCustomer) return
    setCustomerSaveStatus('saving')
    setCustomerSaveError('')
    try {
      await updateCustomer(editingCustomer.id, input.patch)
      clearCustomerListPageCache()
      const latestCustomer = await getCustomer(editingCustomer.id)
      setEditingCustomer({ ...latestCustomer })
      await duplicateCustomerCandidatesQuery.refetch()
      await customerListQuery.refetch()
    } catch (error) {
      setCustomerSaveError(
        error instanceof Error ? error.message : '更新所選比對資料失敗，請稍後再試。',
      )
      throw error
    } finally {
      setCustomerSaveStatus('idle')
    }
  }

  async function archiveDuplicateCustomer(customer: ManagedCustomer) {
    await updateCustomerArchiveStatus(customer, 'archived')
  }

  async function refreshCustomerList() {
    setIsRefreshingCustomers(true)
    try {
      clearCustomerListPageCache()
      await customerListQuery.refetch()
    } finally {
      setIsRefreshingCustomers(false)
    }
  }

  return (
    <div className="flex w-[100dvw] min-w-0 max-w-[100dvw] flex-col gap-5 overflow-hidden p-4 lg:w-[var(--miao-dashboard-main-width)] lg:max-w-[var(--miao-dashboard-main-width)] lg:p-6">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold">客戶管理</h1>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            主管核准問卷後先更新客戶資料；業務可依此到外部 ERP 手動建檔，再回填 ERP未建檔 /
            ERP已建檔狀態。
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => void refreshCustomerList()}
          disabled={isRefreshingCustomers}
        >
          <RefreshCw className={`size-4 ${isRefreshingCustomers ? 'animate-spin' : ''}`} />
          重新整理
        </Button>
      </div>

      <section className="grid gap-4 md:grid-cols-3">
        <Metric label="可見客戶" value={totalCustomers} icon={Building2} />
        <Metric
          label="本頁 ERP未建檔"
          value={
            pageCustomersInScope.filter((customer) => customer.filingStatus === 'not_filed').length
          }
          icon={ClipboardList}
        />
        <Metric
          label="本頁 ERP已建檔"
          value={
            pageCustomersInScope.filter((customer) => customer.filingStatus === 'filed').length
          }
          icon={ClipboardList}
        />
      </section>

      <section className="grid min-w-0 gap-3 rounded-lg border border-slate-200 bg-white p-3 xl:grid-cols-[minmax(220px,1fr)_repeat(5,minmax(0,180px))]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={keyword}
            onChange={(event) => setKeyword(event.currentTarget.value)}
            placeholder="搜尋公司、業務、聯絡人、需求、ERP 客戶代號"
            className="pl-9"
          />
        </div>
        <FilterSelect
          value={ownerFilter}
          onChange={setOwnerFilter}
          placeholder="對應業務"
          options={[
            { value: 'all', label: '全部業務' },
            ...ownerOptions.map(([value, label]) => ({ value, label })),
          ]}
        />
        <FilterSelect
          value={filingFilter}
          onChange={(value) => setFilingFilter(value as 'all' | CustomerFilingStatus)}
          placeholder="建檔狀態"
          options={[
            { value: 'all', label: '全部建檔狀態' },
            { value: 'not_filed', label: 'ERP未建檔' },
            { value: 'filed', label: 'ERP已建檔' },
          ]}
        />
        <FilterSelect
          value={customerWorkflowFilter}
          onChange={(value) => setCustomerWorkflowFilter(value as CustomerWorkflowFilter)}
          placeholder="新客戶建檔"
          options={customerWorkflowOptions}
        />
        <FilterSelect
          value={archiveStatusFilter}
          onChange={(value) => setArchiveStatusFilter(value as CustomerArchiveStatusFilter)}
          placeholder="封存狀態"
          options={customerArchiveStatusOptions}
        />
        <FilterSelect
          value={customerSort}
          onChange={(value) => setCustomerSort(value as CustomerListSort)}
          placeholder="排序方式"
          options={customerSortOptions}
        />
      </section>

      {customerDetailError ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {customerDetailError}
        </div>
      ) : null}
      {customerArchiveError ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {customerArchiveError}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        {customerListQuery.isLoading ? (
          <div className="p-8 text-center text-sm text-slate-500">讀取客戶清單中...</div>
        ) : customerListQuery.isError ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-8 text-center text-sm text-rose-700">
            {customerListQuery.error instanceof Error
              ? customerListQuery.error.message
              : '讀取客戶清單失敗。'}
          </div>
        ) : pageCustomersInScope.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>客戶</TableHead>
                  <TableHead>建檔狀態</TableHead>
                  <TableHead>負責業務</TableHead>
                  <TableHead>主管</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageCustomersInScope.map((customer) => {
                  const filingStatus = customerFilingStatusMeta[customer.filingStatus]
                  return (
                    <TableRow key={customer.id}>
                      <TableCell className="min-w-[220px]">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-slate-950">{customer.companyName}</span>
                          {customer.status === 'archived' ? (
                            <Badge variant="secondary">已封存</Badge>
                          ) : null}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          {customer.erpCustomerCode || '未回填客戶代號'} · {customer.contactName}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={filingStatus.variant}>{filingStatus.label}</Badge>
                      </TableCell>
                      <TableCell>{customer.ownerName}</TableCell>
                      <TableCell>{customer.supervisorName}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            onClick={() => void openCustomerDetail(customer)}
                            disabled={loadingCustomerId === customer.id}
                          >
                            {loadingCustomerId === customer.id ? '讀取中' : '查看'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              void updateCustomerArchiveStatus(
                                customer,
                                customer.status === 'archived' ? 'active' : 'archived',
                              )
                            }
                            disabled={archivingCustomerId === customer.id}
                          >
                            {customer.status === 'archived' ? (
                              <ArchiveRestore className="size-4" />
                            ) : (
                              <Archive className="size-4" />
                            )}
                            {archivingCustomerId === customer.id
                              ? '處理中'
                              : customer.status === 'archived'
                                ? '解除封存'
                                : '封存'}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
            <B2bPagination
              currentPage={currentPage}
              pageCount={pageCount}
              totalCount={totalCustomers}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center">
            <ClipboardList className="mx-auto size-10 text-slate-300" />
            <h2 className="mt-3 font-semibold text-slate-900">目前沒有符合條件的客戶</h2>
            <p className="mt-1 text-sm text-slate-500">
              核准審核中心的問卷後，客戶會自動出現在這裡。
            </p>
          </div>
        )}
      </div>

      <CustomerDetailDialog
        open={Boolean(editingCustomer)}
        customer={editingCustomer}
        sourceApplication={selectedSourceApplication}
        sourceLink={selectedSourceLink}
        fieldValues={selectedFieldValues}
        duplicateSignals={selectedDuplicateSignals}
        duplicateCandidates={selectedDuplicateCandidates}
        canEdit={canBackfillCustomer}
        showErpFilingPanel
        onChange={(patch) =>
          setEditingCustomer((current) => {
            setCustomerSaveError('')
            return current ? { ...current, ...patch } : current
          })
        }
        onCompareErpBackfill={({ customer, erpCustomerCode }) =>
          compareErpCustomerBackfill(customer.id, erpCustomerCode)
        }
        onApplyDuplicateCustomerFields={({ patch }) => applyDuplicateCustomerFields({ patch })}
        onArchiveDuplicateCustomer={(customer) => archiveDuplicateCustomer(customer)}
        onOpenChange={(open) => {
          if (!open) setEditingCustomer(null)
        }}
        onSave={saveCustomer}
        saveDisabled={customerSaveStatus === 'saving'}
        saveButtonLabel={customerSaveStatus === 'saving' ? '儲存中' : '儲存客戶資料'}
        footerActions={
          customerSaveError ? (
            <div
              className="mr-auto rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
              role="alert"
            >
              {customerSaveError}
            </div>
          ) : null
        }
      />
    </div>
  )
}

function getEffectiveMode(role: BusinessRole, currentSalesUserRole?: SalesUserRole): ViewMode {
  if (role === 'admin') return 'admin'
  if (role === 'manager') return 'manager'
  return currentSalesUserRole === 'assistant' ? 'assistant' : 'sales'
}

function getPersonaForMode(
  viewMode: ViewMode,
  email: string | null | undefined,
  salesUsers: ReturnType<typeof useBusinessWorkflowStore.getState>['salesUsers'],
) {
  const current = getSalesUserByEmail(email, salesUsers)
  if (viewMode === 'manager') {
    return current?.role === 'manager'
      ? current
      : salesUsers.find((salesUser) => salesUser.role === 'manager')
  }
  if (viewMode === 'sales') {
    return current && isSalesContributorRole(current.role)
      ? current
      : salesUsers.find((salesUser) => isSalesContributorRole(salesUser.role))
  }
  if (viewMode === 'assistant') {
    return current?.role === 'assistant'
      ? current
      : salesUsers.find((salesUser) => salesUser.role === 'assistant')
  }
  return current
}

function isCustomerInScope(
  customer: ManagedCustomer,
  viewMode: ViewMode,
  persona: ReturnType<typeof getPersonaForMode>,
) {
  if (viewMode === 'admin') return true
  void customer
  return Boolean(persona)
}

function dedupeCustomersById(customers: ManagedCustomer[]) {
  return Array.from(new Map(customers.map((customer) => [customer.id, customer])).values())
}

function mergeDuplicateSignals(
  calculatedSignals: DuplicateCustomerSignal[],
  application: CustomerApplication,
  currentCustomer?: ManagedCustomer | null,
) {
  const savedSignals = getApplicationSavedDuplicateSignals(application)
  const signalsByKey = new Map<string, DuplicateCustomerSignal>()
  for (const signal of filterSelfDuplicateSignals(
    [...savedSignals, ...calculatedSignals],
    currentCustomer,
  )) {
    const key = [
      signal.source ?? '',
      signal.field,
      signal.value,
      signal.matchedCustomerId,
      signal.matchedCustomerCode ?? '',
    ].join('|')
    signalsByKey.set(key, signal)
  }
  return Array.from(signalsByKey.values())
}

function filterSelfDuplicateSignals(
  signals: DuplicateCustomerSignal[],
  currentCustomer?: ManagedCustomer | null,
) {
  if (!currentCustomer) return signals
  return signals.filter((signal) => signal.matchedCustomerId !== currentCustomer.id)
}

function getApplicationSavedDuplicateSignals(application: CustomerApplication) {
  const rawSignals = (
    application as CustomerApplication & {
      duplicateSignals?: DuplicateCustomerSignal[]
    }
  ).duplicateSignals
  return Array.isArray(rawSignals) ? rawSignals : []
}

function Metric({
  label,
  value,
  icon: Icon,
}: {
  label: string
  value: number
  icon: LucideIcon
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <div className="grid size-9 place-items-center rounded-lg bg-[#4a2f21]/10 text-[#4a2f21]">
          <Icon className="size-4" />
        </div>
        <div>
          <div className="text-xs text-slate-500">{label}</div>
          <div className="text-xl font-semibold">{value}</div>
        </div>
      </CardContent>
    </Card>
  )
}

function B2bPagination({
  currentPage,
  pageCount,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: {
  currentPage: number
  pageCount: number
  totalCount: number
  pageSize: B2bPageSize
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: B2bPageSize) => void
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 text-sm text-slate-500">
      <span>
        每頁 {pageSize} 筆，共 {totalCount} 筆，第 {currentPage} / {pageCount} 頁
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={String(pageSize)}
          onValueChange={(value) => onPageSizeChange(Number(value) as B2bPageSize)}
        >
          <SelectTrigger className="h-8 w-[112px]">
            <SelectValue placeholder="每頁筆數" />
          </SelectTrigger>
          <SelectContent>
            {B2B_PAGE_SIZE_OPTIONS.map((option) => (
              <SelectItem key={option} value={String(option)}>
                每頁 {option} 筆
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        >
          上一頁
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={currentPage >= pageCount}
          onClick={() => onPageChange(Math.min(pageCount, currentPage + 1))}
        >
          下一頁
        </Button>
      </div>
    </div>
  )
}

function FilterSelect({
  value,
  options,
  onChange,
  placeholder,
}: {
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
  placeholder?: string
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger aria-label={placeholder} className="min-w-0">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
